import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormLabel,
  Input,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useToast,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { CreatableSelect, Select as RSelect } from 'chakra-react-select';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowsLeftRight, CheckCircle, Lightning, WarningCircle } from 'phosphor-react';

import { useAuth } from '../../../../../../providers/auth';
import { IEmpresaOption } from '../../../../../../hooks/useContaCilImportar';
import { useContaCilPocs } from '../../../../../../hooks/useContaCilLancamentos';
import {
  IBancoOption,
  IContaCilRegra,
  IRegraPayload,
  useContaCilRegraPreview,
  useCriarRegra,
  useEditarRegra,
} from '../../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { EmpresaPicker } from '../../components/EmpresaPicker';
import { C, fmtValor } from '../regrasHelpers';
import { propsMenuSelect } from '../../../../../../utils/selectMenu';

type DrawerMode = 'create' | 'edit' | 'duplicate';
type DCSel = 'ALL' | 'D' | 'C';
type PocTipo = 'none' | 'search' | 'codigo';

interface RegraDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  empresa: IEmpresaOption | null;
  bancos: IBancoOption[];
  categorias: string[];
  initial?: Partial<IContaCilRegra> | null;
  mode: DrawerMode;
  editId?: number | null;
  onSaved: () => void;
}

const SectionTitle = ({ children, color }: { children: React.ReactNode; color?: string }) => (
  <Text
    fontSize="11px"
    fontWeight="bold"
    color={color || C.subtle}
    textTransform="uppercase"
    letterSpacing="0.06em"
    mb={2}
  >
    {children}
  </Text>
);

export function RegraDrawer({
  isOpen,
  onClose,
  empresa,
  bancos,
  categorias,
  initial,
  mode,
  editId,
  onSaved,
}: RegraDrawerProps) {
  const toast = useToast();
  const isTechnical = useAuth().isTechnical();
  const criar = useCriarRegra();
  const editar = useEditarRegra();

  const [escEmpresa, setEscEmpresa] = useState<IEmpresaOption | null>(null);
  const [kw, setKw] = useState<string[]>([]);
  const [kwInput, setKwInput] = useState('');
  const [dc, setDc] = useState<DCSel>('ALL');
  const [bancoSlug, setBancoSlug] = useState<string | null>(null);
  const [categoria, setCategoria] = useState('');
  const [pocTipo, setPocTipo] = useState<PocTipo>('none');
  const [pocSearchInput, setPocSearchInput] = useState('');
  const [pocCodigo, setPocCodigo] = useState<string | null>(null);

  // Reidrata o form quando abre.
  useEffect(() => {
    if (!isOpen) return;
    setKw(initial?.kw ?? []);
    setKwInput('');
    setDc((initial?.dc as DCSel) || 'ALL');
    setBancoSlug(initial?.banco ?? null);
    setCategoria(initial?.categoria ?? '');
    if (initial?.poc_codigo) {
      setPocTipo('codigo');
      setPocCodigo(initial.poc_codigo);
      setPocSearchInput('');
    } else if (initial?.poc_search && initial.poc_search.length) {
      setPocTipo('search');
      setPocSearchInput(initial.poc_search.join(', '));
      setPocCodigo(null);
    } else {
      setPocTipo('none');
      setPocCodigo(null);
      setPocSearchInput('');
    }
    // Empresa do drawer: prioriza a empresa DA REGRA (initial.empresa_id) sobre
    // a empresa do filtro da tela, pra POC fixa carregar as POCs corretas mesmo
    // quando o filtro tá vazio/diferente (era o bug do "Nenhum" na edição).
    let nextEsc: IEmpresaOption | null;
    if (initial?.empresa_id === null) {
      nextEsc = null; // regra global explícita
    } else if (typeof initial?.empresa_id === 'number') {
      if (initial.empresa_id === empresa?.value) {
        nextEsc = empresa;
      } else if (initial.empresa_nome) {
        nextEsc = {
          value: initial.empresa_id,
          label: [initial.empresa_external_id, initial.empresa_nome].filter(Boolean).join(' · '),
          external_id: initial.empresa_external_id ?? '',
        };
      } else {
        nextEsc = {
          value: initial.empresa_id,
          label: `Empresa #${initial.empresa_id}`,
          external_id: '',
        };
      }
    } else {
      nextEsc = empresa; // criar novo herda da tela
    }
    setEscEmpresa(nextEsc);
  }, [isOpen, initial, empresa]);

  const scopeEmpresaId = escEmpresa?.value ?? null;
  const { data: pocs = [] } = useContaCilPocs(escEmpresa?.value);

  // Preview ao vivo (debounce 400ms).
  const [debouncedKw, setDebouncedKw] = useState<string[]>([]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedKw(kw), 400);
    return () => clearTimeout(t);
  }, [kw]);
  const previewDraft = useMemo(
    () => ({
      empresa_id: escEmpresa?.value ?? null,
      banco: bancoSlug,
      kw: debouncedKw,
      dc: dc === 'ALL' ? null : dc,
      limit_exemplos: 6,
    }),
    [escEmpresa?.value, bancoSlug, debouncedKw, dc],
  );
  const { data: preview, isFetching: previewLoading } = useContaCilRegraPreview(
    previewDraft,
    isOpen && escEmpresa?.value != null,
  );

  const bancoOptions = useMemo(
    () => [
      { value: '', label: 'Todos os bancos' },
      ...bancos.map(b => ({ value: b.slug, label: b.nome })),
    ],
    [bancos],
  );
  const catOptions = useMemo(() => categorias.map(c => ({ value: c, label: c })), [categorias]);
  const pocOptions = useMemo(
    () => pocs.map(p => ({ value: p.codigo, label: `${p.codigo} · ${p.nome}` })),
    [pocs],
  );

  const addKw = () => {
    const parts = kwInput
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length) setKw(prev => Array.from(new Set([...prev, ...parts])));
    setKwInput('');
  };
  const removeKw = (k: string) => setKw(prev => prev.filter(x => x !== k));

  const saving = criar.isPending || editar.isPending;
  const TITULOS: Record<DrawerMode, string> = {
    edit: 'Editar regra',
    duplicate: 'Duplicar como personalizada',
    create: 'Nova regra',
  };
  const tituloModo = TITULOS[mode];

  const handleSave = async () => {
    if (!kw.length) {
      toast({ title: 'Adicione ao menos uma palavra-chave.', status: 'warning' });
      return;
    }
    if (!categoria.trim()) {
      toast({ title: 'Escolha uma categoria.', status: 'warning' });
      return;
    }
    if (!escEmpresa && !isTechnical) {
      toast({
        title: 'Selecione uma empresa (regra global só para admin/dev).',
        status: 'warning',
      });
      return;
    }
    if (!escEmpresa && pocTipo === 'codigo') {
      toast({
        title: 'POC fixa exige empresa — regra global não aceita conta fixa.',
        status: 'warning',
      });
      return;
    }
    const payload: IRegraPayload = {
      empresa_id: scopeEmpresaId,
      banco: bancoSlug || null,
      kw,
      categoria: categoria.trim(),
      dc: dc === 'ALL' ? null : dc,
      poc_search:
        pocTipo === 'search'
          ? pocSearchInput
              .split(',')
              .map(s => s.trim().toLowerCase())
              .filter(Boolean)
          : null,
      poc_codigo: pocTipo === 'codigo' ? pocCodigo : null,
    };
    try {
      if (mode === 'edit' && editId != null) {
        await editar.mutateAsync({ id: editId, payload });
        toast({ title: 'Regra atualizada.', status: 'success' });
      } else {
        await criar.mutateAsync(payload);
        toast({ title: 'Regra criada.', status: 'success' });
      }
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Não foi possível salvar.'), status: 'error' });
    }
  };

  const dcBtn = (val: DCSel, label: string, active: string, color: string) => (
    <Button
      flex={1}
      h="34px"
      borderRadius="md"
      fontSize="sm"
      fontWeight="700"
      variant="unstyled"
      bg={dc === val ? active : 'transparent'}
      color={dc === val ? color : C.muted}
      onClick={() => setDc(val)}
    >
      {label}
    </Button>
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay bg="rgba(15, 23, 42, 0.45)" backdropFilter="blur(8px)" />
      <DrawerContent
        maxW="480px"
        bg="rgba(255, 255, 255, 0.9)"
        backdropFilter="blur(40px) saturate(200%)"
        sx={{ WebkitBackdropFilter: 'blur(40px) saturate(200%)' }}
        borderLeftWidth="1px"
        borderColor="rgba(255, 255, 255, 0.6)"
      >
        <DrawerCloseButton color={C.muted} />
        <DrawerHeader borderBottomWidth="1px" borderColor={C.hairline}>
          <Flex align="center" gap={3}>
            <Box
              w="44px"
              h="44px"
              borderRadius="xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="linear-gradient(135deg, #FCE7F3, #FBCFE8)"
              color={C.rosa}
            >
              <ArrowsLeftRight size={24} weight="duotone" />
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="700" color={C.texto}>
                {tituloModo}
              </Text>
              <Text fontSize="xs" color={C.muted}>
                Histórico → categoria → POC fixa fecha sozinho
              </Text>
            </Box>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          <Box mb={5}>
            <SectionTitle>Quando o histórico contém</SectionTitle>
            <Flex gap={2}>
              <Input
                size="sm"
                borderRadius="lg"
                bg="white"
                placeholder="ex: tarifa ted (Enter ou vírgula)"
                value={kwInput}
                onChange={e => setKwInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addKw();
                  }
                }}
              />
              <Button size="sm" borderRadius="lg" onClick={addKw} variant="outline">
                Add
              </Button>
            </Flex>
            {kw.length > 0 && (
              <Wrap mt={2} spacing={2}>
                {kw.map(k => (
                  <WrapItem key={k}>
                    <Tag size="sm" borderRadius="full" variant="subtle" colorScheme="pink">
                      <TagLabel>{k}</TagLabel>
                      <TagCloseButton onClick={() => removeKw(k)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
            <Text fontSize="10px" color={C.subtle} mt={1}>
              Casa por trecho (qualquer palavra-chave que apareça no histórico).
            </Text>
          </Box>

          <Box mb={5} borderTopWidth="1px" borderColor={C.hairline} pt={4}>
            <SectionTitle>Aplica quando</SectionTitle>
            <Flex p="3px" bg="rgba(15, 23, 42, 0.04)" borderRadius="lg" gap="3px" mb={3}>
              {dcBtn('ALL', 'Ambos', 'white', C.texto)}
              {dcBtn('D', 'Débito', C.verdeTint, C.verdeText)}
              {dcBtn('C', 'Crédito', C.vermelhoTint, C.vermelhoText)}
            </Flex>
            <EmpresaPicker
              label="Empresa"
              value={escEmpresa}
              onChange={opt => {
                setEscEmpresa(opt);
                if (!opt && pocTipo === 'codigo') setPocTipo('none');
              }}
              isClearable={isTechnical}
              placeholderOverride={
                isTechnical ? 'Empresa (vazio = global)' : 'Selecione a empresa...'
              }
            />
            {isTechnical && !escEmpresa && (
              <Text fontSize="10px" color={C.ambarText} mt={1}>
                Sem empresa = regra global (vale para todas; sem POC fixa).
              </Text>
            )}
            <FormLabel fontSize="xs" color={C.muted} mb={1} mt={3}>
              Banco
            </FormLabel>
            <RSelect
              size="sm"
              options={bancoOptions}
              value={bancoOptions.find(o => o.value === (bancoSlug || '')) ?? bancoOptions[0]}
              onChange={opt => setBancoSlug((opt as { value: string })?.value || null)}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              {...propsMenuSelect}
              styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
              chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
            />
          </Box>

          <Box mb={5} borderTopWidth="1px" borderColor={C.hairline} pt={4}>
            <SectionTitle>Categoriza como</SectionTitle>
            <CreatableSelect
              size="sm"
              options={catOptions}
              value={categoria ? { value: categoria, label: categoria } : null}
              onChange={opt => setCategoria((opt as { value: string })?.value || '')}
              onCreateOption={val => setCategoria(val)}
              placeholder="Escolha ou crie..."
              formatCreateLabel={val => `Criar "${val}"`}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              {...propsMenuSelect}
              styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
              chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
            />
          </Box>

          <Box
            mb={5}
            bg="rgba(99, 102, 241, 0.05)"
            borderWidth="1px"
            borderColor="rgba(99, 102, 241, 0.15)"
            borderRadius="xl"
            p={4}
          >
            <SectionTitle color={C.indigo}>Plano de contas (POC)</SectionTitle>
            <Flex gap={2} mb={3}>
              {(
                [
                  ['none', 'Nenhuma'],
                  ['search', 'Buscar'],
                  ['codigo', 'Conta fixa'],
                ] as [PocTipo, string][]
              ).map(([val, label]) => (
                <Button
                  key={val}
                  flex={1}
                  size="sm"
                  borderRadius="lg"
                  variant="unstyled"
                  h="32px"
                  fontSize="xs"
                  fontWeight="700"
                  borderWidth="1.5px"
                  borderColor={pocTipo === val ? C.indigo : 'rgba(15,23,42,0.1)'}
                  bg={pocTipo === val ? 'rgba(99,102,241,0.08)' : 'white'}
                  color={pocTipo === val ? C.indigo : C.muted}
                  isDisabled={val === 'codigo' && !escEmpresa}
                  onClick={() => setPocTipo(val)}
                >
                  {label}
                </Button>
              ))}
            </Flex>
            {pocTipo === 'search' && (
              <Input
                size="sm"
                borderRadius="lg"
                bg="white"
                placeholder="termos, ex: tarifa, despesa bancaria"
                value={pocSearchInput}
                onChange={e => setPocSearchInput(e.target.value)}
              />
            )}
            {pocTipo === 'codigo' && (
              <RSelect
                size="sm"
                options={pocOptions}
                value={pocOptions.find(o => o.value === pocCodigo) ?? null}
                onChange={opt => setPocCodigo((opt as { value: string })?.value || null)}
                placeholder={escEmpresa ? 'Escolha a conta...' : 'Selecione uma empresa'}
                isDisabled={!escEmpresa}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                {...propsMenuSelect}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            )}
            <Text fontSize="10px" color={C.indigo} mt={2}>
              Conta fixa garante o vínculo e fecha o lançamento sozinho (auto-conciliar).
            </Text>
          </Box>

          {/* Preview ao vivo */}
          <Box
            bg="rgba(99, 102, 241, 0.06)"
            borderWidth="1px"
            borderColor="rgba(99, 102, 241, 0.18)"
            borderRadius="2xl"
            p={5}
          >
            <SectionTitle>Prévia ao vivo</SectionTitle>
            {!escEmpresa ? (
              <Text fontSize="xs" color={C.muted}>
                Selecione uma empresa para ver a prévia.
              </Text>
            ) : (
              <>
                <Flex align="baseline" gap={2} justify="center" py={2}>
                  <AnimatePresence mode="popLayout">
                    <motion.div
                      key={preview?.total_casados ?? -1}
                      initial={{ opacity: 0, y: 8, scale: 0.9 }}
                      animate={{ opacity: previewLoading ? 0.4 : 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.9 }}
                      transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <Text
                        fontSize="40px"
                        fontWeight="800"
                        letterSpacing="-0.03em"
                        color={C.indigo}
                        lineHeight="1"
                      >
                        {debouncedKw.length ? (preview?.total_casados ?? 0) : 0}
                      </Text>
                    </motion.div>
                  </AnimatePresence>
                  {previewLoading && <Spinner size="xs" color={C.indigo} />}
                </Flex>
                <Text fontSize="xs" color={C.muted} textAlign="center" mb={2}>
                  lançamentos casariam
                </Text>
                {preview && preview.exemplos.length > 0 && (
                  <Box>
                    {preview.exemplos.slice(0, 5).map(ex => (
                      <Flex key={ex.id} justify="space-between" fontSize="11px" py={0.5}>
                        <Text color="#475569" isTruncated maxW="300px">
                          {ex.historico}
                        </Text>
                        <Text
                          color={ex.dc === 'C' ? C.vermelhoText : C.verdeText}
                          fontWeight="600"
                          ml={2}
                        >
                          {fmtValor(ex.valor)}
                        </Text>
                      </Flex>
                    ))}
                  </Box>
                )}
                {preview && preview.ja_categorizados_diferente > 0 ? (
                  <Flex
                    gap={2}
                    bg="rgba(245,158,11,0.10)"
                    borderRadius="lg"
                    p={3}
                    mt={2}
                    color={C.ambarText}
                  >
                    <Box pt="2px">
                      <WarningCircle size={16} weight="duotone" color={C.ambar} />
                    </Box>
                    <Text fontSize="11px" lineHeight="1.4">
                      {preview.ja_categorizados_diferente} já têm outra categoria — esta regra os
                      recategoriza no próximo import.
                    </Text>
                  </Flex>
                ) : (
                  preview &&
                  preview.total_casados > 0 && (
                    <Flex
                      gap={2}
                      bg="rgba(16,185,129,0.10)"
                      borderRadius="lg"
                      p={3}
                      mt={2}
                      color={C.verdeText}
                    >
                      <CheckCircle size={16} weight="fill" />
                      <Text fontSize="11px">Sem conflito — pronta pra fechar sozinha.</Text>
                    </Flex>
                  )
                )}
              </>
            )}
          </Box>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={C.hairline}>
          <Flex justify="flex-end" w="full" gap={3}>
            <Button variant="outline" size="sm" borderRadius="full" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              size="sm"
              borderRadius="full"
              px={6}
              bg={C.rosa}
              color="white"
              fontWeight="600"
              boxShadow="0 8px 20px -4px rgba(236,72,153,0.42)"
              _hover={{ bg: C.rosaHover, transform: 'translateY(-1px)' }}
              _active={{ bg: C.rosaHover }}
              leftIcon={<Lightning size={16} weight="fill" />}
              isLoading={saving}
              onClick={handleSave}
            >
              Salvar regra
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
